import { useEffect, useMemo, useState } from "react";
import "./sponsors.css";

type Sponsor = {
  logo: string;
  alt: string;
  link: string;
  width?: number;
  height?: number;
};

type SponsorsData = {
  lead: Sponsor[];
  gold: Sponsor[];
  silver: Sponsor[];
  bronze: Sponsor[];
};

const sponsors: SponsorsData = {
  lead: [
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1752837167/mashreq_xcf6fw.png",
      alt: "Mashreq",
      link: "https://www.mashreq.com/en/uae/neo/",
      width: 340, // width-driven
    },
  ],
  gold: [
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1750072620/murex_xjopwn.png",
      alt: "Murex",
      link: "https://www.murex.com/en",
    },
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1758696127/FAB-logo_ttgqb3.png",
      alt: "FAB",
      link: "https://bankfab.com/en-ae",
      width: 220, // width-driven
    },
  ],
  silver: [
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1750072620/finastra_dz6iew.png",
      alt: "Finastra",
      link: "https://www.finastra.com/solutions/treasury-capital-markets",
      width: 320, // width-driven
    },
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1757613919/lseg_scwqth.png",
      alt: "LSEG",
      link: "https://www.lseg.com/en",
      width: 200, // width-driven
    },
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1761122194/Credit_Agricole_Corporate_and_Investment_Bank_Logo_2x_rvdzxt.png",
      alt: "Credit Agricole",
      link: "https://www.ca-cib.com/en/our-network-united-arab-emirates",
      width: 480,
    },
  ],
  bronze: [
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1752134921/ab_logo_white_riyuji.png",
      alt: "Ajman Bank",
      link: "https://www.ajmanbank.ae/site/",
      width: 200, // width-driven
    },
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1754560447/TP_ICAP_Logo_Master_ej8vzo.png",
      alt: "TP ICAP",
      link: "https://tpicap.com/tpicap/",
      width: 220,
    },
    {
      logo: "https://res.cloudinary.com/astcdubai/image/upload/v1758176311/UBS_rgb_2x_ht1rcm.png",
      alt: "UBS",
      link: "https://www.ubs.com/global/en/investment-bank.html",
      width: 180,
    },
  ],
};

/** MOBILE-ONLY rotator that preserves your tier sizing + width-driven variant */
function SponsorRotator({
  items,
  intervalMs = 2500,
  tierClass, // "tier-large" | "tier-medium" | "tier-small" | "tier-xs"
}: {
  items: Sponsor[];
  intervalMs?: number;
  tierClass: string;
}) {
  const [index, setIndex] = useState(0);

  // Preload
  useEffect(() => {
    items.forEach((s) => {
      const img = new Image();
      img.src = s.logo;
    });
  }, [items]);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [items.length, intervalMs]);

  return (
    <div className={`sponsor-rotator ${tierClass}`}>
      {items.map((s, i) => {
        const isWidthDriven = typeof s.width === "number" && s.width > 0;
        const styleVars: React.CSSProperties = {};
        if (isWidthDriven) (styleVars as any)["--logo-w"] = `${s.width}px`;
        if (typeof s.height === "number" && s.height > 0)
          (styleVars as any)["--logo-h"] = `${s.height}px`;

        return (
          <a
            key={s.alt}
            href={s.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`sponsor slide ${i === index ? "active" : ""}`}
            aria-label={s.alt}
          >
            <div
              className={`logo-box ${isWidthDriven ? "width-driven" : ""}`}
              style={styleVars}
            >
              <img src={s.logo} alt={s.alt} loading="lazy" />
            </div>
          </a>
        );
      })}
    </div>
  );
}

function LogoItem({ s }: { s: Sponsor }) {
  const isWidthDriven = typeof s.width === "number" && s.width > 0;
  const styleVars = useMemo(() => {
    const vars: React.CSSProperties = {};
    if (isWidthDriven) (vars as any)["--logo-w"] = `${s.width}px`;
    else if (typeof s.height === "number" && s.height > 0)
      (vars as any)["--logo-h"] = `${s.height}px`;
    return vars;
  }, [isWidthDriven, s.width, s.height]);

  return (
    <a
      href={s.link}
      target="_blank"
      rel="noopener noreferrer"
      className="sponsor"
      aria-label={s.alt}
    >
      <div
        className={`logo-box ${isWidthDriven ? "width-driven" : ""}`}
        style={styleVars}
      >
        <img src={s.logo} alt={s.alt} loading="lazy" />
      </div>
    </a>
  );
}

const SponsorsSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <section id="sponsors" className="pt-120 pb-120">
      <div className="container">
        {/* Lead */}
        <div className="row text-center">
          <div className="col-md-12">
            <h1 className="title">Lead Sponsor</h1>
            {isMobile ? (
              <SponsorRotator items={sponsors.lead} tierClass="tier-large" />
            ) : (
              <div className="sponsor-row pt-20 tier-large">
                {sponsors.lead.map((s) => (
                  <LogoItem key={s.alt} s={s} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Gold */}
        <div className="row text-center pt-80">
          <div className="col-md-12">
            <h1 className="title">Gold Sponsor</h1>
            {isMobile ? (
              <SponsorRotator items={sponsors.gold} tierClass="tier-medium" />
            ) : (
              <div className="sponsor-row pt-20 tier-medium">
                {sponsors.gold.map((s) => (
                  <LogoItem key={s.alt} s={s} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Silver */}
        <div className="row text-center pt-80">
          <div className="col-md-12">
            <h1 className="title">Silver Sponsors</h1>
            {isMobile ? (
              <SponsorRotator items={sponsors.silver} tierClass="tier-small" />
            ) : (
              <div className="sponsor-row pt-20 tier-small">
                {sponsors.silver.map((s) => (
                  <LogoItem key={s.alt} s={s} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bronze */}
        <div className="row text-center pt-80">
          <div className="col-md-12">
            <h1 className="title">Bronze Sponsors</h1>
            {isMobile ? (
              <SponsorRotator items={sponsors.bronze} tierClass="tier-xs" />
            ) : (
              <div className="sponsor-row pt-20 tier-small">
                {sponsors.bronze.map((s) => (
                  <LogoItem key={s.alt} s={s} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SponsorsSection;
